import type { CatalogTransport } from './warehouse-catalog-client';

export type DepartmentOption = { dept_id: string; name: string; active: 0 | 1 };
export type DepartmentOptions = {
  source: 'netsuite'; view: 'departments_v1'; fetchedAt: number;
  scope: { kind: 'full' } | { kind: 'department'; department_ids: string[] };
  departments_v1: DepartmentOption[];
};
export type DepartmentOptionsResult =
  | { state: 'loading' | 'unauthenticated' | 'denied' | 'error' }
  | { state: 'ready'; data: DepartmentOptions };

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const deptId = (value: unknown): value is string => typeof value === 'string' && /^[1-9][0-9]{0,14}$/.test(value);
const label = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0 && value.length <= 200;

export function parseDepartmentOptions(value: unknown): DepartmentOptions {
  if (!record(value) || value.source !== 'netsuite' || value.view !== 'departments_v1'
    || !Number.isSafeInteger(value.fetchedAt) || (value.fetchedAt as number) <= 0
    || !Array.isArray(value.departments_v1) || value.departments_v1.length > 1000 || !record(value.scope)) {
    throw new Error('Unusable department catalog');
  }
  let scope: DepartmentOptions['scope'];
  if (value.scope.kind === 'full' && Object.keys(value.scope).length === 1) {
    scope = { kind: 'full' };
  } else if (value.scope.kind === 'department' && Array.isArray(value.scope.department_ids)
    && Object.keys(value.scope).length === 2 && value.scope.department_ids.length > 0
    && value.scope.department_ids.length <= 100 && value.scope.department_ids.every(deptId)
    && new Set(value.scope.department_ids).size === value.scope.department_ids.length) {
    scope = { kind: 'department', department_ids: [...value.scope.department_ids] as string[] };
  } else throw new Error('Invalid department scope');

  const ids = new Set<string>();
  const departments_v1 = value.departments_v1.map((row): DepartmentOption => {
    if (!record(row) || !deptId(row.dept_id) || ids.has(row.dept_id) || !label(row.name)
      || !(row.active === 0 || row.active === 1)) {
      throw new Error('Invalid department');
    }
    if (scope.kind === 'department' && !scope.department_ids.includes(row.dept_id)) {
      throw new Error('Department is outside the authorized scope');
    }
    ids.add(row.dept_id);
    return { dept_id: row.dept_id, name: row.name, active: row.active };
  });
  return { source: 'netsuite', view: 'departments_v1', fetchedAt: value.fetchedAt as number, scope, departments_v1 };
}

/** Fixed, same-origin, no-store request. A denied scope is distinct from sign-in failure. */
export function createDepartmentOptionsLoader(
  transport: CatalogTransport, publish: (result: DepartmentOptionsResult) => void,
) {
  let generation = 0;
  let controller: AbortController | undefined;
  let disposed = false;
  return {
    async refresh() {
      if (disposed) return;
      const request = ++generation;
      controller?.abort();
      controller = new AbortController();
      const current = controller;
      publish({ state: 'loading' });
      let result: DepartmentOptionsResult;
      try {
        const response = await transport('/api/finance/departments', {
          method: 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: current.signal,
          headers: { Accept: 'application/json' },
        });
        if (response.status === 401) result = { state: 'unauthenticated' };
        else if (response.status === 403) result = { state: 'denied' };
        else if (!response.ok || response.redirected || !/^application\/json(?:;|$)/i.test(response.headers.get('Content-Type') ?? '')) {
          result = { state: 'error' };
        } else result = { state: 'ready', data: parseDepartmentOptions(await response.json()) };
      } catch { result = { state: 'error' }; }
      if (!disposed && request === generation) publish(result);
    },
    dispose() { disposed = true; generation++; controller?.abort(); },
  };
}
